import type {
  GameClamp,
  GameAssetBinding,
  GameAssetRegistryEntry,
  GameCondition,
  GameDefinition,
  GameDiceRule,
  GameEndCondition,
  GameEntityRoster,
  GameEntityTemplate,
  GameGate,
  GameHudDeclaration,
  GameRule,
  GameRuleAction,
  GameScene,
  GameSceneGraph,
  GameSetupField,
  GameStateFieldSchema,
  GameViewBinding,
} from "./game-definition.js";

export const ROQEET_GAME_DOCUMENT_KIND = "roqeet.game" as const;
export const ROQEET_GAME_RQT_VERSION = "0.1" as const;
export const ROQEET_GAME_FORMAT_VERSION = "0.1" as const;
export const ROQEET_GAME_DOCUMENT_SCHEMA_ID = "roqeet.game@0.1" as const;
export const ROQEET_TEXT_EXTENSION = ".rqt" as const;
export const ROQEET_GAME_EXTENSION = ".rqg" as const;
export const ROQEET_PACKAGE_EXTENSION = ".rqp" as const;
export const ROQEET_LEGACY_PACKAGE_EXTENSION = ".rqtpack" as const;
const UNSAFE_OBJECT_KEYS = new Set(["__proto__", "constructor", "prototype"]);

/**
 * Product-facing format map. Keep this as the single source of truth when UI,
 * importers, or guides need to explain what is official and what is internal.
 */
export const ROQEET_GAME_FORMAT_SURFACES = {
  rqt: {
    extension: ROQEET_TEXT_EXTENSION,
    status: "advanced",
    name: "RoQeet Text",
    role: "optional advanced authoring/import/export source",
  },
  rqg: {
    extension: ROQEET_GAME_EXTENSION,
    status: "official",
    name: "RoQeet Game",
    role: "canonical JSON game document; not a runtime execution target until normalized",
  },
  rqp: {
    extension: ROQEET_PACKAGE_EXTENSION,
    status: "official",
    name: "RoQeet Package",
    role: "public distributable package containing manifest, game.rqg, optional source, and assets",
  },
  rqtpackLegacy: {
    extension: ROQEET_LEGACY_PACKAGE_EXTENSION,
    status: "legacy",
    role: "transition alias for .rqp",
  },
  gameDefinition: {
    name: "GameDefinition",
    status: "internal-compatibility",
    role: "compiler compatibility input and migration target",
  },
  gameCard: {
    name: "GameCard",
    status: "wrapper",
    role: "local save/share/preview wrapper around the .rqg document",
  },
} as const;

/**
 * Official game-shaped `.rqg` document.
 *
 * This is the editable/exchange surface for data games. `GameDefinition` remains
 * the compiler's internal compatibility input, but creators should see and
 * exchange typed RoQeet JSON nodes instead of a flat GameDefinition blob.
 */
export interface RoqeetGameDocument {
  kind: typeof ROQEET_GAME_DOCUMENT_KIND;
  rqtVersion: typeof ROQEET_GAME_RQT_VERSION;
  formatVersion: typeof ROQEET_GAME_FORMAT_VERSION;
  startSceneId?: string;
  nodes: RoqeetGameNode[];
  extensions?: Record<string, unknown>;
}

export type RoqeetGameActionKind =
  | "choice"
  | "set"
  | "inc"
  | "dec"
  | "push"
  | "unset"
  | "scene"
  | "roll";

export interface RoqeetGameChoice {
  id?: string;
  label: string;
  to?: string;
  when?: GameCondition[];
  effects?: GameRuleAction[];
}

export interface RoqeetGameSceneNode {
  type: "game.scene";
  kind?: "game.scene";
  id: string;
  title?: string;
  content: string;
  choices?: RoqeetGameChoice[];
}

export type RoqeetGameNode =
  | { type: "game.meta"; title: string; titleKey?: string; description?: string; descriptionKey?: string; icon?: string }
  | { type: "game.initialState"; values: Record<string, unknown> }
  | { type: "game.setup"; fields: GameSetupField[] }
  | { type: "game.narrationGuide"; text: string; textKey?: string }
  | { type: "game.stateSummary"; template: string; templateKey?: string }
  | { type: "game.stateSchema"; fields: GameStateFieldSchema[] }
  | { type: "game.mutableKeys"; keys: string[] }
  | { type: "game.clamps"; clamps: GameClamp[] }
  | {
      type: "game.entities";
      template?: GameEntityTemplate;
      roster?: GameEntityRoster;
      mutableKeys?: string[];
      clamps?: GameClamp[];
    }
  | { type: "game.rules"; rules: GameRule[] }
  | { type: "game.initRules"; rules: GameRule[] }
  | { type: "game.promptGates"; gates: GameGate[] }
  | { type: "game.dice"; rule: GameDiceRule }
  | { type: "game.endings"; conditions: GameEndCondition[] }
  | { type: "game.assets"; assets: GameAssetRegistryEntry[] }
  | { type: "game.assetBindings"; bindings: GameAssetBinding[] }
  | { type: "game.hud"; hud: GameHudDeclaration }
  | { type: "game.view"; view: GameViewBinding }
  | RoqeetGameSceneNode
  | {
      type: "game.aiContract";
      aiOutput: "narration-and-proposed-actions";
      engineRole: "validate-and-apply";
      allowedActions: RoqeetGameActionKind[];
      invalidAction: "ignore-and-report";
    };

export const ROQEET_GAME_DOCUMENT_NODE_TYPES = [
  "game.meta",
  "game.initialState",
  "game.setup",
  "game.narrationGuide",
  "game.stateSummary",
  "game.stateSchema",
  "game.mutableKeys",
  "game.clamps",
  "game.entities",
  "game.rules",
  "game.initRules",
  "game.promptGates",
  "game.dice",
  "game.endings",
  "game.assets",
  "game.assetBindings",
  "game.hud",
  "game.view",
  "game.scene",
  "game.aiContract",
] as const satisfies readonly RoqeetGameNode["type"][];

export const ROQEET_GAME_DOCUMENT_REQUIRED_NODE_TYPES = [
  "game.meta",
  "game.initialState",
  "game.narrationGuide",
  "game.aiContract",
] as const satisfies readonly RoqeetGameNode["type"][];

export interface RoqeetGameSource {
  document: RoqeetGameDocument;
  definition: GameDefinition;
}

export interface RoqeetGameDocumentIssue {
  severity: "info" | "warning" | "error";
  code: string;
  message: string;
  path?: string;
}

export const ROQEET_GAME_DEFAULT_ACTIONS = [
  "choice",
  "set",
  "inc",
  "dec",
  "push",
  "unset",
  "scene",
  "roll",
] as const satisfies readonly RoqeetGameActionKind[];

export const ROQEET_GAME_DOCUMENT_SCHEMA = {
  id: ROQEET_GAME_DOCUMENT_SCHEMA_ID,
  kind: ROQEET_GAME_DOCUMENT_KIND,
  rqtVersion: ROQEET_GAME_RQT_VERSION,
  formatVersion: ROQEET_GAME_FORMAT_VERSION,
  officialSurfaces: ROQEET_GAME_FORMAT_SURFACES,
  requiredTopLevelFields: ["kind", "rqtVersion", "formatVersion", "nodes"],
  optionalTopLevelFields: ["startSceneId", "extensions"],
  nodeTypes: ROQEET_GAME_DOCUMENT_NODE_TYPES,
  requiredNodeTypes: ROQEET_GAME_DOCUMENT_REQUIRED_NODE_TYPES,
  sceneGraph: {
    nodeType: "game.scene",
    startSceneIdField: "startSceneId",
    choicesAreNested: true,
    topLevelChoiceNodes: false,
  },
  aiContract: {
    aiOutput: "narration-and-proposed-actions",
    engineRole: "validate-and-apply",
    allowedActions: ROQEET_GAME_DEFAULT_ACTIONS,
    invalidAction: "ignore-and-report",
  },
} as const;

export function gameDefinitionToRoqeetJson(def: GameDefinition): RoqeetGameDocument {
  const nodes: RoqeetGameNode[] = [
    {
      type: "game.meta",
      title: def.title,
      ...(def.titleKey !== undefined ? { titleKey: def.titleKey } : {}),
      ...(def.description !== undefined ? { description: def.description } : {}),
      ...(def.descriptionKey !== undefined ? { descriptionKey: def.descriptionKey } : {}),
      ...(def.icon !== undefined ? { icon: def.icon } : {}),
    },
    { type: "game.initialState", values: cloneRecord(def.initialState) },
    {
      type: "game.narrationGuide",
      text: def.instructions,
      ...(def.instructionsKey !== undefined ? { textKey: def.instructionsKey } : {}),
    },
    {
      type: "game.aiContract",
      aiOutput: "narration-and-proposed-actions",
      engineRole: "validate-and-apply",
      allowedActions: [...ROQEET_GAME_DEFAULT_ACTIONS],
      invalidAction: "ignore-and-report",
    },
  ];

  if (def.setup?.length) nodes.push({ type: "game.setup", fields: clone(def.setup) });
  if (def.stateSummaryTemplate) {
    nodes.push({
      type: "game.stateSummary",
      template: def.stateSummaryTemplate,
      ...(def.stateSummaryTemplateKey !== undefined
        ? { templateKey: def.stateSummaryTemplateKey }
        : {}),
    });
  }
  if (def.stateSchema?.length) nodes.push({ type: "game.stateSchema", fields: clone(def.stateSchema) });
  if (def.mutableKeys?.length) nodes.push({ type: "game.mutableKeys", keys: [...def.mutableKeys] });
  if (def.clamps?.length) nodes.push({ type: "game.clamps", clamps: clone(def.clamps) });
  if (def.entityTemplate || def.entityRoster || def.mutableEntityKeys?.length || def.entityClamps?.length) {
    nodes.push({
      type: "game.entities",
      ...(def.entityTemplate ? { template: clone(def.entityTemplate) } : {}),
      ...(def.entityRoster ? { roster: clone(def.entityRoster) } : {}),
      ...(def.mutableEntityKeys?.length ? { mutableKeys: [...def.mutableEntityKeys] } : {}),
      ...(def.entityClamps?.length ? { clamps: clone(def.entityClamps) } : {}),
    });
  }
  if (def.rules?.length) nodes.push({ type: "game.rules", rules: clone(def.rules) });
  if (def.initRules?.length) nodes.push({ type: "game.initRules", rules: clone(def.initRules) });
  if (def.gates?.length) nodes.push({ type: "game.promptGates", gates: clone(def.gates) });
  if (def.dice) nodes.push({ type: "game.dice", rule: clone(def.dice) });
  if (def.endWhen?.length) nodes.push({ type: "game.endings", conditions: clone(def.endWhen) });
  if (def.assets?.length) nodes.push({ type: "game.assets", assets: clone(def.assets) });
  if (def.assetBindings?.length) {
    nodes.push({ type: "game.assetBindings", bindings: clone(def.assetBindings) });
  }
  if (def.hud) nodes.push({ type: "game.hud", hud: clone(def.hud) });
  if (def.view) nodes.push({ type: "game.view", view: clone(def.view) });
  const sceneGraph = def.sceneGraph?.scenes?.length ? def.sceneGraph : undefined;
  if (sceneGraph) {
    nodes.push(...sceneGraph.scenes.map(gameSceneToRoqeetNode));
  }
  const startSceneId = sceneGraph?.startSceneId ?? sceneGraph?.scenes[0]?.id;

  return {
    kind: ROQEET_GAME_DOCUMENT_KIND,
    rqtVersion: ROQEET_GAME_RQT_VERSION,
    formatVersion: ROQEET_GAME_FORMAT_VERSION,
    ...(startSceneId ? { startSceneId } : {}),
    nodes,
  };
}

export function roqeetJsonToGameDefinition(doc: RoqeetGameDocument): GameDefinition {
  const meta = firstNode(doc, "game.meta");
  const initialState = firstNode(doc, "game.initialState");
  const title = typeof meta?.title === "string" ? meta.title.trim() : "";
  if (!title) throw new Error("유효한 .rqg 게임 문서가 아닙니다(title 없음).");
  const initialValues = isRecord(initialState?.values) ? initialState.values : undefined;
  if (!initialValues) {
    throw new Error("유효한 .rqg 게임 문서가 아닙니다(initialState 없음).");
  }

  const guide = firstNode(doc, "game.narrationGuide");
  const summary = firstNode(doc, "game.stateSummary");
  const stateSchema = firstNode(doc, "game.stateSchema");
  const mutableKeys = firstNode(doc, "game.mutableKeys");
  const setup = firstNode(doc, "game.setup");
  const clamps = firstNode(doc, "game.clamps");
  const entities = firstNode(doc, "game.entities");
  const rules = firstNode(doc, "game.rules");
  const initRules = firstNode(doc, "game.initRules");
  const gates = firstNode(doc, "game.promptGates");
  const dice = firstNode(doc, "game.dice");
  const endings = firstNode(doc, "game.endings");
  const assets = firstNode(doc, "game.assets");
  const assetBindings = firstNode(doc, "game.assetBindings");
  const hud = firstNode(doc, "game.hud");
  const view = firstNode(doc, "game.view");
  const sceneGraph = sceneGraphFromRoqeetDocument(doc);

  return {
    id: "",
    title,
    ...(typeof meta?.titleKey === "string" ? { titleKey: meta.titleKey } : {}),
    ...(typeof meta?.description === "string" ? { description: meta.description } : {}),
    ...(typeof meta?.descriptionKey === "string" ? { descriptionKey: meta.descriptionKey } : {}),
    ...(typeof meta?.icon === "string" ? { icon: meta.icon } : {}),
    initialState: cloneRecord(initialValues),
    instructions: typeof guide?.text === "string" ? guide.text : "",
    ...(typeof guide?.textKey === "string" ? { instructionsKey: guide.textKey } : {}),
    ...(Array.isArray(setup?.fields) && setup.fields.length ? { setup: clone(setup.fields) } : {}),
    ...(typeof summary?.template === "string" && summary.template
      ? { stateSummaryTemplate: summary.template }
      : {}),
    ...(typeof summary?.templateKey === "string" ? { stateSummaryTemplateKey: summary.templateKey } : {}),
    ...(sceneGraph ? { sceneGraph } : {}),
    ...(Array.isArray(stateSchema?.fields) && stateSchema.fields.length
      ? { stateSchema: clone(stateSchema.fields) }
      : {}),
    ...(Array.isArray(mutableKeys?.keys) && mutableKeys.keys.length ? { mutableKeys: [...mutableKeys.keys] } : {}),
    ...(Array.isArray(clamps?.clamps) && clamps.clamps.length ? { clamps: clone(clamps.clamps) } : {}),
    ...(isRecord(entities?.template) ? { entityTemplate: clone(entities.template) } : {}),
    ...(isRecord(entities?.roster) ? { entityRoster: clone(entities.roster) as GameEntityRoster } : {}),
    ...(Array.isArray(entities?.mutableKeys) && entities.mutableKeys.length
      ? { mutableEntityKeys: [...entities.mutableKeys] }
      : {}),
    ...(Array.isArray(entities?.clamps) && entities.clamps.length ? { entityClamps: clone(entities.clamps) } : {}),
    ...(Array.isArray(rules?.rules) && rules.rules.length ? { rules: clone(rules.rules) } : {}),
    ...(Array.isArray(initRules?.rules) && initRules.rules.length ? { initRules: clone(initRules.rules) } : {}),
    ...(Array.isArray(gates?.gates) && gates.gates.length ? { gates: clone(gates.gates) } : {}),
    ...(isRecord(dice?.rule) ? { dice: clone(dice.rule) as GameDiceRule } : {}),
    ...(Array.isArray(endings?.conditions) && endings.conditions.length ? { endWhen: clone(endings.conditions) } : {}),
    ...(Array.isArray(assets?.assets) && assets.assets.length ? { assets: clone(assets.assets) } : {}),
    ...(Array.isArray(assetBindings?.bindings) && assetBindings.bindings.length
      ? { assetBindings: clone(assetBindings.bindings) }
      : {}),
    ...(isRecord(hud?.hud) ? { hud: clone(hud.hud) as GameHudDeclaration } : {}),
    ...(isRecord(view?.view) ? { view: clone(view.view) as GameViewBinding } : {}),
  };
}

function sceneGraphFromRoqeetDocument(doc: RoqeetGameDocument): GameSceneGraph | undefined {
  const scenes = doc.nodes.filter((node): node is RoqeetGameSceneNode => node.type === "game.scene").map(roqeetSceneToGameScene);
  if (!scenes.length) return undefined;
  return {
    ...(doc.startSceneId ? { startSceneId: doc.startSceneId } : {}),
    scenes,
  };
}

function roqeetSceneToGameScene(node: RoqeetGameSceneNode): GameScene {
  return {
    id: node.id,
    ...(node.title !== undefined ? { title: node.title } : {}),
    content: node.content,
    ...(node.choices?.length ? { choices: clone(node.choices) } : {}),
  };
}

function gameSceneToRoqeetNode(scene: GameScene): RoqeetGameSceneNode {
  return {
    type: "game.scene",
    id: scene.id,
    ...(scene.title !== undefined ? { title: scene.title } : {}),
    content: scene.content,
    ...(scene.choices?.length ? { choices: clone(scene.choices) } : {}),
  };
}

export function formatRoqeetGameDocument(doc: RoqeetGameDocument): string {
  return JSON.stringify(doc, null, 2);
}

export function parseRoqeetGameDocument(text: string): RoqeetGameDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("JSON 구문 오류: .rqg 게임 문서를 해석할 수 없습니다.");
  }
  const issues = validateRoqeetGameDocument(parsed);
  const errors = issues.filter((issue) => issue.severity === "error");
  if (errors.length > 0) {
    throw new Error(
      `.rqg 게임 문서 구조 오류: ${errors
        .slice(0, 3)
        .map((issue) => issue.path ? `${issue.path}: ${issue.message}` : issue.message)
        .join(" / ")}`,
    );
  }
  return parsed as RoqeetGameDocument;
}

export function isRoqeetGameDocument(value: unknown): value is RoqeetGameDocument {
  return (
    isRecord(value) &&
    value["kind"] === ROQEET_GAME_DOCUMENT_KIND &&
    value["rqtVersion"] === ROQEET_GAME_RQT_VERSION &&
    value["formatVersion"] === ROQEET_GAME_FORMAT_VERSION &&
    Array.isArray(value["nodes"])
  );
}

const NODE_TYPE_SET = new Set<string>(ROQEET_GAME_DOCUMENT_NODE_TYPES);
const REQUIRED_NODE_TYPE_SET = new Set<string>(ROQEET_GAME_DOCUMENT_REQUIRED_NODE_TYPES);
const MULTI_NODE_TYPE_SET = new Set<string>(["game.scene"]);
const ACTION_KIND_SET = new Set<string>(ROQEET_GAME_DEFAULT_ACTIONS);
const CONDITION_OP_SET = new Set<string>(["lte", "gte", "lt", "gt", "eq", "neq", "truthy", "falsy"]);
const RULE_ACTION_OP_SET = new Set<string>(["set", "inc", "dec"]);
const VALUE_TYPE_SET = new Set<string>(["number", "string", "boolean", "list", "array", "string[]", "object", "enum"]);
const MUTABLE_BY_SET = new Set<string>(["ai", "engine", "readonly"]);
const ASSET_KIND_SET = new Set<string>(["image", "audio", "sfx", "video", "other"]);
const ASSET_SLOT_SET = new Set<string>(["background", "sprite", "bgm", "sfx", "overlay"]);
const ASSET_POSITION_SET = new Set<string>(["left", "center", "right"]);
const TONE_SET = new Set<string>(["info", "success", "warning", "danger"]);

export function validateRoqeetGameDocument(value: unknown): RoqeetGameDocumentIssue[] {
  const issues: RoqeetGameDocumentIssue[] = [];
  if (!isRecord(value)) {
    pushError(issues, "RGD001", ".rqg 게임 문서는 객체여야 합니다.", "$");
    return issues;
  }

  if (value["kind"] !== ROQEET_GAME_DOCUMENT_KIND) {
    pushError(issues, "RGD002", `kind는 "${ROQEET_GAME_DOCUMENT_KIND}"이어야 합니다.`, "$.kind");
  }
  if (value["rqtVersion"] !== ROQEET_GAME_RQT_VERSION) {
    pushError(issues, "RGD003", `rqtVersion은 "${ROQEET_GAME_RQT_VERSION}"이어야 합니다.`, "$.rqtVersion");
  }
  if (value["formatVersion"] !== ROQEET_GAME_FORMAT_VERSION) {
    pushError(issues, "RGD004", `formatVersion은 "${ROQEET_GAME_FORMAT_VERSION}"이어야 합니다.`, "$.formatVersion");
  }
  if (value["startSceneId"] !== undefined && (typeof value["startSceneId"] !== "string" || !String(value["startSceneId"]).trim())) {
    pushError(issues, "RGD096", "startSceneId must be a non-empty string when provided.", "$.startSceneId");
  }
  if (!Array.isArray(value["nodes"])) {
    pushError(issues, "RGD005", "nodes는 배열이어야 합니다.", "$.nodes");
    return issues;
  }

  const nodes = value["nodes"] as unknown[];
  const seen = new Map<string, number>();
  nodes.forEach((node, index) => {
    const path = `$.nodes[${index}]`;
    if (!isRecord(node)) {
      pushError(issues, "RGD006", "노드는 객체여야 합니다.", path);
      return;
    }
    const type = node["type"];
    if (typeof type !== "string") {
      pushError(issues, "RGD007", "노드 type은 문자열이어야 합니다.", `${path}.type`);
      return;
    }
    if (!NODE_TYPE_SET.has(type)) {
      pushError(issues, "RGD008", `알 수 없는 노드 type "${type}"입니다.`, `${path}.type`);
      return;
    }
    if (!MULTI_NODE_TYPE_SET.has(type)) {
      const prev = seen.get(type);
      if (prev !== undefined) {
        pushError(issues, "RGD009", `중복 노드 type "${type}"입니다. 같은 노드는 하나만 선언하세요.`, path);
      } else {
        seen.set(type, index);
      }
    }
    validateNode(issues, node, type as RoqeetGameNode["type"], path);
  });

  for (const type of REQUIRED_NODE_TYPE_SET) {
    if (!seen.has(type)) {
      pushError(issues, "RGD010", `필수 노드 "${type}"가 없습니다.`, "$.nodes");
    }
  }

  validateSceneGraph(issues, value, nodes);

  return issues;
}

function validateNode(
  issues: RoqeetGameDocumentIssue[],
  node: Record<string, unknown>,
  type: RoqeetGameNode["type"],
  path: string,
): void {
  switch (type) {
    case "game.meta":
      requireNonEmptyString(issues, node, "title", `${path}.title`);
      optionalString(issues, node, "titleKey", `${path}.titleKey`);
      optionalString(issues, node, "description", `${path}.description`);
      optionalString(issues, node, "descriptionKey", `${path}.descriptionKey`);
      optionalString(issues, node, "icon", `${path}.icon`);
      break;
    case "game.initialState":
      if (!isRecord(node["values"])) pushError(issues, "RGD020", "initialState values는 객체여야 합니다.", `${path}.values`);
      break;
    case "game.setup":
      validateSetupFields(issues, node["fields"], `${path}.fields`);
      break;
    case "game.narrationGuide":
      requireString(issues, node, "text", `${path}.text`);
      optionalString(issues, node, "textKey", `${path}.textKey`);
      break;
    case "game.stateSummary":
      requireString(issues, node, "template", `${path}.template`);
      optionalString(issues, node, "templateKey", `${path}.templateKey`);
      break;
    case "game.stateSchema":
      validateStateSchema(issues, node["fields"], `${path}.fields`);
      break;
    case "game.mutableKeys":
      validateStringArray(issues, node["keys"], `${path}.keys`);
      break;
    case "game.clamps":
      validateClamps(issues, node["clamps"], `${path}.clamps`);
      break;
    case "game.entities":
      if (node["template"] !== undefined && !isRecord(node["template"])) {
        pushError(issues, "RGD040", "entities template은 객체여야 합니다.", `${path}.template`);
      }
      if (node["roster"] !== undefined && !isRecord(node["roster"])) {
        pushError(issues, "RGD041", "entities roster는 객체여야 합니다.", `${path}.roster`);
      }
      if (node["mutableKeys"] !== undefined) validateStringArray(issues, node["mutableKeys"], `${path}.mutableKeys`);
      if (node["clamps"] !== undefined) validateClamps(issues, node["clamps"], `${path}.clamps`);
      break;
    case "game.rules":
      validateRules(issues, node["rules"], `${path}.rules`);
      break;
    case "game.initRules":
      validateRules(issues, node["rules"], `${path}.rules`);
      break;
    case "game.promptGates":
      validateGates(issues, node["gates"], `${path}.gates`);
      break;
    case "game.dice":
      validateDice(issues, node["rule"], `${path}.rule`);
      break;
    case "game.endings":
      validateEndings(issues, node["conditions"], `${path}.conditions`);
      break;
    case "game.assets":
      validateAssets(issues, node["assets"], `${path}.assets`);
      break;
    case "game.assetBindings":
      validateAssetBindings(issues, node["bindings"], `${path}.bindings`);
      break;
    case "game.hud":
      validateHud(issues, node["hud"], `${path}.hud`);
      break;
    case "game.view":
      validateView(issues, node["view"], `${path}.view`);
      break;
    case "game.scene":
      validateScene(issues, node, path);
      break;
    case "game.aiContract":
      if (node["aiOutput"] !== "narration-and-proposed-actions") {
        pushError(issues, "RGD080", "aiOutput은 narration-and-proposed-actions여야 합니다.", `${path}.aiOutput`);
      }
      if (node["engineRole"] !== "validate-and-apply") {
        pushError(issues, "RGD081", "engineRole은 validate-and-apply여야 합니다.", `${path}.engineRole`);
      }
      if (node["invalidAction"] !== "ignore-and-report") {
        pushError(issues, "RGD082", "invalidAction은 ignore-and-report여야 합니다.", `${path}.invalidAction`);
      }
      validateActionKinds(issues, node["allowedActions"], `${path}.allowedActions`);
      break;
  }
}

function validateScene(issues: RoqeetGameDocumentIssue[], node: Record<string, unknown>, path: string): void {
  if (node["kind"] !== undefined && node["kind"] !== "game.scene") {
    pushError(issues, "RGD100", 'game.scene kind must be "game.scene" when provided.', `${path}.kind`);
  }
  requireNonEmptyString(issues, node, "id", `${path}.id`);
  optionalString(issues, node, "title", `${path}.title`);
  requireString(issues, node, "content", `${path}.content`);
  if (node["choices"] !== undefined) validateSceneChoices(issues, node["choices"], `${path}.choices`);
}

function validateSceneChoices(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD101", "scene choices must be an array.", path);
    return;
  }
  value.forEach((choice, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(choice)) {
      pushError(issues, "RGD102", "scene choice must be an object.", p);
      return;
    }
    optionalString(issues, choice, "id", `${p}.id`);
    requireNonEmptyString(issues, choice, "label", `${p}.label`);
    optionalString(issues, choice, "to", `${p}.to`);
    validateConditions(issues, choice["when"], `${p}.when`, false);
    if (choice["effects"] !== undefined) validateRuleActions(issues, choice["effects"], `${p}.effects`);
  });
}

function validateSceneGraph(
  issues: RoqeetGameDocumentIssue[],
  root: Record<string, unknown>,
  nodes: unknown[],
): void {
  const sceneEntries = nodes
    .map((node, index) => ({ node, index }))
    .filter((entry): entry is { node: Record<string, unknown>; index: number } =>
      isRecord(entry.node) && entry.node["type"] === "game.scene",
    );
  if (!sceneEntries.length) return;

  const sceneIds = new Map<string, string>();
  sceneEntries.forEach(({ node, index }) => {
    const rawId = node["id"];
    if (typeof rawId !== "string" || !rawId.trim()) return;
    const id = rawId.trim();
    const path = `$.nodes[${index}]`;
    const prev = sceneIds.get(id);
    if (prev) {
      pushError(issues, "RGD103", `Duplicate scene id "${id}".`, `${path}.id`);
    } else {
      sceneIds.set(id, path);
    }
  });

  const startSceneId = root["startSceneId"];
  if (typeof startSceneId !== "string" || !startSceneId.trim()) {
    pushError(issues, "RGD104", "startSceneId is required when game.scene nodes are present.", "$.startSceneId");
  } else if (!sceneIds.has(startSceneId.trim())) {
    pushError(issues, "RGD105", `startSceneId "${startSceneId}" does not match any game.scene id.`, "$.startSceneId");
  }

  sceneEntries.forEach(({ node, index }) => {
    const choices = node["choices"];
    if (!Array.isArray(choices)) return;
    choices.forEach((choice, choiceIndex) => {
      if (!isRecord(choice)) return;
      const to = choice["to"];
      if (typeof to !== "string" || !to.trim()) return;
      if (!sceneIds.has(to.trim())) {
        pushError(
          issues,
          "RGD106",
          `Choice target "${to}" does not match any game.scene id.`,
          `$.nodes[${index}].choices[${choiceIndex}].to`,
        );
      }
    });
  });
}

function validateSetupFields(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD030", "setup fields는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((field, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(field)) {
      pushError(issues, "RGD031", "setup field는 객체여야 합니다.", p);
      return;
    }
    requireNonEmptyString(issues, field, "key", `${p}.key`);
    optionalString(issues, field, "label", `${p}.label`);
    optionalString(issues, field, "default", `${p}.default`);
    if (!Array.isArray(field["options"])) {
      pushError(issues, "RGD032", "setup options는 배열이어야 합니다.", `${p}.options`);
      return;
    }
    field["options"].forEach((option, optionIndex) => {
      const op = `${p}.options[${optionIndex}]`;
      if (!isRecord(option)) {
        pushError(issues, "RGD033", "setup option은 객체여야 합니다.", op);
        return;
      }
      requireString(issues, option, "value", `${op}.value`);
      optionalString(issues, option, "label", `${op}.label`);
    });
  });
}

function validateStateSchema(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD034", "stateSchema fields는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((field, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(field)) {
      pushError(issues, "RGD035", "stateSchema field는 객체여야 합니다.", p);
      return;
    }
    requireNonEmptyString(issues, field, "key", `${p}.key`);
    if (typeof field["valueType"] !== "string" || !VALUE_TYPE_SET.has(field["valueType"])) {
      pushError(issues, "RGD036", "valueType이 올바르지 않습니다.", `${p}.valueType`);
    }
    optionalNumber(issues, field, "min", `${p}.min`);
    optionalNumber(issues, field, "max", `${p}.max`);
    if (field["mutableBy"] !== undefined && (typeof field["mutableBy"] !== "string" || !MUTABLE_BY_SET.has(field["mutableBy"]))) {
      pushError(issues, "RGD037", "mutableBy must be ai, engine, or readonly.", `${p}.mutableBy`);
    }
    if (field["values"] !== undefined) validateStringArray(issues, field["values"], `${p}.values`);
    optionalBoolean(issues, field, "mutable", `${p}.mutable`);
    optionalBoolean(issues, field, "derived", `${p}.derived`);
    optionalBoolean(issues, field, "transient", `${p}.transient`);
    optionalString(issues, field, "label", `${p}.label`);
  });
}

function validateRules(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD050", "rules는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((rule, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(rule)) {
      pushError(issues, "RGD051", "rule은 객체여야 합니다.", p);
      return;
    }
    validateConditions(issues, rule["when"], `${p}.when`, false);
    if (!Array.isArray(rule["then"])) {
      pushError(issues, "RGD052", "rule then은 배열이어야 합니다.", `${p}.then`);
      return;
    }
    rule["then"].forEach((action, actionIndex) => {
      const ap = `${p}.then[${actionIndex}]`;
      if (!isRecord(action)) {
        pushError(issues, "RGD053", "rule action은 객체여야 합니다.", ap);
        return;
      }
      if (typeof action["op"] !== "string" || !RULE_ACTION_OP_SET.has(action["op"])) {
        pushError(issues, "RGD054", "rule action op가 올바르지 않습니다.", `${ap}.op`);
      }
      requireNonEmptyString(issues, action, "key", `${ap}.key`);
      if (!isPrimitiveActionValue(action["value"])) {
        pushError(issues, "RGD055", "rule action value는 문자열, 숫자, boolean이어야 합니다.", `${ap}.value`);
      }
    });
  });
}

function validateRuleActions(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD052", "rule actions must be an array.", path);
    return;
  }
  value.forEach((action, actionIndex) => {
    const ap = `${path}[${actionIndex}]`;
    if (!isRecord(action)) {
      pushError(issues, "RGD053", "rule action must be an object.", ap);
      return;
    }
    if (typeof action["op"] !== "string" || !RULE_ACTION_OP_SET.has(action["op"])) {
      pushError(issues, "RGD054", "rule action op is invalid.", `${ap}.op`);
    }
    requireNonEmptyString(issues, action, "key", `${ap}.key`);
    if (!isPrimitiveActionValue(action["value"])) {
      pushError(issues, "RGD055", "rule action value must be a string, number, or boolean.", `${ap}.value`);
    }
  });
}

function validateGates(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD056", "promptGates gates는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((gate, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(gate)) {
      pushError(issues, "RGD057", "gate는 객체여야 합니다.", p);
      return;
    }
    validateConditions(issues, gate["when"], `${p}.when`, false);
    requireString(issues, gate, "inject", `${p}.inject`);
  });
}

function validateEndings(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD060", "endings conditions는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((condition, index) => {
    const p = `${path}[${index}]`;
    validateCondition(issues, condition, p);
    if (!isRecord(condition)) return;
    optionalString(issues, condition, "label", `${p}.label`);
    optionalString(issues, condition, "notice", `${p}.notice`);
    if (condition["tone"] !== undefined && (typeof condition["tone"] !== "string" || !TONE_SET.has(condition["tone"]))) {
      pushError(issues, "RGD061", "ending tone이 올바르지 않습니다.", `${p}.tone`);
    }
  });
}

function validateConditions(
  issues: RoqeetGameDocumentIssue[],
  value: unknown,
  path: string,
  required: boolean,
): void {
  if (value === undefined && !required) return;
  if (!Array.isArray(value)) {
    pushError(issues, "RGD062", "conditions는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((condition, index) => validateCondition(issues, condition, `${path}[${index}]`));
}

function validateCondition(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!isRecord(value)) {
    pushError(issues, "RGD063", "condition은 객체여야 합니다.", path);
    return;
  }
  requireNonEmptyString(issues, value, "key", `${path}.key`);
  if (typeof value["op"] !== "string" || !CONDITION_OP_SET.has(value["op"])) {
    pushError(issues, "RGD064", "condition op가 올바르지 않습니다.", `${path}.op`);
  }
  if (value["value"] !== undefined && !isPrimitiveActionValue(value["value"])) {
    pushError(issues, "RGD065", "condition value는 문자열, 숫자, boolean이어야 합니다.", `${path}.value`);
  }
}

function validateClamps(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD066", "clamps는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((clamp, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(clamp)) {
      pushError(issues, "RGD067", "clamp는 객체여야 합니다.", p);
      return;
    }
    requireNonEmptyString(issues, clamp, "key", `${p}.key`);
    optionalNumber(issues, clamp, "min", `${p}.min`);
    optionalNumber(issues, clamp, "max", `${p}.max`);
  });
}

function validateDice(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!isRecord(value)) {
    pushError(issues, "RGD068", "dice rule은 객체여야 합니다.", path);
    return;
  }
  optionalString(issues, value, "resultKey", `${path}.resultKey`);
  optionalString(issues, value, "notationKey", `${path}.notationKey`);
  optionalString(issues, value, "dcKey", `${path}.dcKey`);
  optionalString(issues, value, "successKey", `${path}.successKey`);
}

function validateAssets(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD070", "assets는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((asset, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(asset)) {
      pushError(issues, "RGD071", "asset은 객체여야 합니다.", p);
      return;
    }
    requireNonEmptyString(issues, asset, "key", `${p}.key`);
    if (typeof asset["kind"] !== "string" || !ASSET_KIND_SET.has(asset["kind"])) {
      pushError(issues, "RGD072", "asset kind가 올바르지 않습니다.", `${p}.kind`);
    }
    optionalString(issues, asset, "assetId", `${p}.assetId`);
    optionalString(issues, asset, "label", `${p}.label`);
  });
}

function validateAssetBindings(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD073", "assetBindings는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((binding, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(binding)) {
      pushError(issues, "RGD074", "assetBinding은 객체여야 합니다.", p);
      return;
    }
    requireNonEmptyString(issues, binding, "id", `${p}.id`);
    if (typeof binding["slot"] !== "string" || !ASSET_SLOT_SET.has(binding["slot"])) {
      pushError(issues, "RGD075", "assetBinding slot이 올바르지 않습니다.", `${p}.slot`);
    }
    optionalString(issues, binding, "assetKey", `${p}.assetKey`);
    optionalString(issues, binding, "assetId", `${p}.assetId`);
    optionalString(issues, binding, "characterId", `${p}.characterId`);
    optionalString(issues, binding, "emotion", `${p}.emotion`);
    if (binding["position"] !== undefined && (typeof binding["position"] !== "string" || !ASSET_POSITION_SET.has(binding["position"]))) {
      pushError(issues, "RGD076", "assetBinding position이 올바르지 않습니다.", `${p}.position`);
    }
    validateConditions(issues, binding["when"], `${p}.when`, false);
  });
}

function validateHud(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!isRecord(value)) {
    pushError(issues, "RGD077", "hud는 객체여야 합니다.", path);
    return;
  }
  validateViewBindingFields(issues, value, path, false);
}

function validateView(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!isRecord(value)) {
    pushError(issues, "RGD078", "view는 객체여야 합니다.", path);
    return;
  }
  validateViewBindingFields(issues, value, path, true);
}

function validateViewBindingFields(
  issues: RoqeetGameDocumentIssue[],
  value: Record<string, unknown>,
  path: string,
  allowChoiceAndDice: boolean,
): void {
  if (value["statBars"] !== undefined) validateStatBars(issues, value["statBars"], `${path}.statBars`);
  if (value["statPanel"] !== undefined) validateStatPanel(issues, value["statPanel"], `${path}.statPanel`);
  optionalString(issues, value, "inventoryKey", `${path}.inventoryKey`);
  optionalString(issues, value, "inventoryTitle", `${path}.inventoryTitle`);
  optionalString(issues, value, "inventoryTitleKey", `${path}.inventoryTitleKey`);
  if (allowChoiceAndDice) {
    optionalBoolean(issues, value, "showChoices", `${path}.showChoices`);
    optionalBoolean(issues, value, "showDice", `${path}.showDice`);
  }
}

function validateStatBars(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD083", "statBars는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((bar, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(bar)) {
      pushError(issues, "RGD084", "statBar는 객체여야 합니다.", p);
      return;
    }
    requireString(issues, bar, "label", `${p}.label`);
    requireNonEmptyString(issues, bar, "valueKey", `${p}.valueKey`);
    optionalString(issues, bar, "maxKey", `${p}.maxKey`);
    optionalNumber(issues, bar, "max", `${p}.max`);
    optionalString(issues, bar, "tone", `${p}.tone`);
  });
}

function validateStatPanel(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD085", "statPanel은 배열이어야 합니다.", path);
    return;
  }
  value.forEach((panel, index) => {
    const p = `${path}[${index}]`;
    if (!isRecord(panel)) {
      pushError(issues, "RGD086", "statPanel 항목은 객체여야 합니다.", p);
      return;
    }
    requireString(issues, panel, "label", `${p}.label`);
    requireNonEmptyString(issues, panel, "key", `${p}.key`);
  });
}

function validateActionKinds(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD087", "allowedActions는 배열이어야 합니다.", path);
    return;
  }
  value.forEach((item, index) => {
    if (typeof item !== "string" || !ACTION_KIND_SET.has(item)) {
      pushError(issues, "RGD088", `알 수 없는 action "${String(item)}"입니다.`, `${path}[${index}]`);
    }
  });
}

function validateStringArray(issues: RoqeetGameDocumentIssue[], value: unknown, path: string): void {
  if (!Array.isArray(value)) {
    pushError(issues, "RGD089", "문자열 배열이어야 합니다.", path);
    return;
  }
  value.forEach((item, index) => {
    if (typeof item !== "string" || !item.trim()) {
      pushError(issues, "RGD090", "빈 값이 아닌 문자열이어야 합니다.", `${path}[${index}]`);
    }
  });
}

function requireString(
  issues: RoqeetGameDocumentIssue[],
  value: Record<string, unknown>,
  key: string,
  path: string,
): void {
  if (typeof value[key] !== "string") pushError(issues, "RGD091", `${key}는 문자열이어야 합니다.`, path);
}

function requireNonEmptyString(
  issues: RoqeetGameDocumentIssue[],
  value: Record<string, unknown>,
  key: string,
  path: string,
): void {
  if (typeof value[key] !== "string" || !String(value[key]).trim()) {
    pushError(issues, "RGD092", `${key}는 빈 값이 아닌 문자열이어야 합니다.`, path);
  }
}

function optionalString(
  issues: RoqeetGameDocumentIssue[],
  value: Record<string, unknown>,
  key: string,
  path: string,
): void {
  if (value[key] !== undefined && typeof value[key] !== "string") {
    pushError(issues, "RGD093", `${key}는 문자열이어야 합니다.`, path);
  }
}

function optionalNumber(
  issues: RoqeetGameDocumentIssue[],
  value: Record<string, unknown>,
  key: string,
  path: string,
): void {
  if (value[key] !== undefined && typeof value[key] !== "number") {
    pushError(issues, "RGD094", `${key}는 숫자여야 합니다.`, path);
  }
}

function optionalBoolean(
  issues: RoqeetGameDocumentIssue[],
  value: Record<string, unknown>,
  key: string,
  path: string,
): void {
  if (value[key] !== undefined && typeof value[key] !== "boolean") {
    pushError(issues, "RGD095", `${key}는 boolean이어야 합니다.`, path);
  }
}

function isPrimitiveActionValue(value: unknown): boolean {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function pushError(
  issues: RoqeetGameDocumentIssue[],
  code: string,
  message: string,
  path?: string,
): void {
  issues.push({ severity: "error", code, message, ...(path ? { path } : {}) });
}

function firstNode<T extends RoqeetGameNode["type"]>(
  doc: RoqeetGameDocument,
  type: T,
): Extract<RoqeetGameNode, { type: T }> | undefined {
  return doc.nodes.find((n): n is Extract<RoqeetGameNode, { type: T }> => isRecord(n) && n["type"] === type);
}

function clone<T>(value: T): T {
  const json = JSON.stringify(value);
  return (json === undefined ? undefined : cloneSafe(JSON.parse(json))) as T;
}

function cloneRecord(value: Record<string, unknown>): Record<string, unknown> {
  const cloned = clone(value);
  return isRecord(cloned) ? cloned : {};
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function cloneSafe(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(cloneSafe);
  if (isRecord(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      if (UNSAFE_OBJECT_KEYS.has(key)) continue;
      out[key] = cloneSafe(item);
    }
    return out;
  }
  return value;
}
